<template>
  <div class="dashboard-page">
    <Navbar />
    <div class="app-container">
      <Sidebar />
      <div id="main-content" class="main-content" tabindex="-1">
        <a
          id="accesskey-c"
          href="#accesskey-c"
          accesskey="C"
          class="accesskey-brick sr-only-focusable"
          title="主要內容區塊，快捷鍵：Alt+C"
          aria-label="主要內容區塊"
        >:::</a>
        <div v-if="loading" class="loading-container">
          <a-spin size="large" />
        </div>
        <div v-else class="dashboard-content">
          <div class="welcome-section">
            <h1>{{ t("favoriteList.title") }}</h1>
          </div>

          <div class="courses-table-container">
            <div class="section-container">
              <div class="table-wrapper table-scroll-container">
                <a-table
                  :dataSource="favoriteRows"
                  :columns="columns"
                  :pagination="false"
                  :rowKey="(record) => record.id"
                  class="course-table"
                  :locale="{ emptyText }"
                >
                  <template #bodyCell="{ column, record }">
                    <template v-if="column.dataIndex === 'title'">
                      <button
                        type="button"
                        class="course-link-button"
                        @click="goToCourse(record.id)"
                      >
                        {{ record.title }}
                      </button>
                    </template>

                    <template v-else-if="column.dataIndex === 'category'">
                      <span>{{ record.category || "-" }}</span>
                    </template>

                    <template v-else-if="column.dataIndex === 'favorited_at'">
                      <span>{{ record.favorited_at }}</span>
                    </template>

                    <template v-else-if="column.dataIndex === 'actions'">
                      <a-button
                        type="link"
                        class="remove-favorite-btn"
                        :title="t('favoriteList.removeFavorite')"
                        @click="toggleFavorite(record.id)"
                      >
                        <svg
                          width="14"
                          height="18"
                          viewBox="0 0 14 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            d="M0 18V2C0 1.45 0.196 0.979333 0.588 0.588C0.98 0.196667 1.45067 0.000666667 2 0H12C12.55 0 13.021 0.196 13.413 0.588C13.805 0.98 14.0007 1.45067 14 2V18L7 15L0 18Z"
                            fill="#0079BB"
                          />
                        </svg>
                      </a-button>
                    </template>
                  </template>
                </a-table>
              </div>

              <div class="pagination-container" v-if="favorites.total > 0">
                <div class="pagination-info">
                  {{ favorites.from || 0 }}-{{ favorites.to || 0 }}
                  {{ t("favoriteList.of") }} {{ favorites.total }}
                </div>
                <div class="pagination-spacer"></div>
                <a-pagination
                  :current="currentPage"
                  :total="favorites.total"
                  :pageSize="favorites.per_page"
                  @change="handlePageChange"
                  show-less-items
                  size="small"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <FooterSection />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { message } from "ant-design-vue";
import { msCourseAPI } from "@/services/api.js";
import Navbar from "../components/Navbar.vue";
import Sidebar from "../components/Sidebar.vue";
import FooterSection from "../components/FooterSection.vue";

const router = useRouter();
const { t, locale } = useI18n();
const loading = ref(true);
const currentPage = ref(1);
const favorites = ref({
  current_page: 1,
  data: [],
  from: 0,
  last_page: 1,
  per_page: 10,
  to: 0,
  total: 0,
});

const columns = computed(() => [
  {
    title: t("favoriteList.courseName"),
    dataIndex: "title",
    key: "title",
    width: "40%",
  },
  {
    title: t("favoriteList.category"),
    dataIndex: "category",
    key: "category",
    width: "20%",
  },
  {
    title: t("favoriteList.favoritedAt"),
    dataIndex: "favorited_at",
    key: "favorited_at",
    width: "20%",
  },
  {
    title: t("favoriteList.action"),
    dataIndex: "actions",
    key: "actions",
    width: "20%",
  },
]);

const emptyText = computed(
  () => `${t("favoriteList.emptyMessage")} ${t("favoriteList.emptyMessageEnd")}`
);

const getCourseTitle = (course) => {
  if (locale.value === "en-US") {
    return course.en_title || course.title;
  }

  return course.title;
};

const getCategoryName = (course) => {
  if (course.categories && course.categories.length > 0) {
    return locale.value === "en-US"
      ? course.categories[0].en_name || course.categories[0].name
      : course.categories[0].name;
  }

  return "-";
};

const normalizeFavorite = (course) => ({
  id: course.id,
  title: getCourseTitle(course),
  category: getCategoryName(course),
  favorited_at: course.favorited_at
    ? new Date(course.favorited_at).toLocaleDateString(
        locale.value === "en-US" ? "en-CA" : "zh-TW"
      )
    : "-",
});

const favoriteRows = computed(() =>
  favorites.value.data.map(normalizeFavorite)
);

const redirectToLogin = () => {
  localStorage.removeItem("ms_platform_token");
  router.push("/sign-in");
};

const fetchFavoriteCourses = async (page = 1) => {
  try {
    loading.value = true;
    const response = await msCourseAPI.listFavorites({ page, per_page: 10 });
    const payload = response?.data || {};
    const meta = payload.meta || {};
    const rows = Array.isArray(payload.data) ? payload.data : [];
    const pageSize = meta.per_page || 10;
    const current = meta.current_page || page;
    const total = meta.total || 0;

    favorites.value = {
      ...favorites.value,
      current_page: current,
      data: rows,
      from: rows.length > 0 ? (current - 1) * pageSize + 1 : 0,
      last_page: meta.last_page || 1,
      per_page: pageSize,
      to: rows.length > 0 ? Math.min(current * pageSize, total) : 0,
      total,
    };
    currentPage.value = favorites.value.current_page;

    if (favorites.value.data.length === 0 && currentPage.value > 1) {
      await fetchFavoriteCourses(currentPage.value - 1);
    }
  } catch (error) {
    if (error.response?.status === 401) {
      redirectToLogin();
      return;
    }

    message.error(t("favoriteList.fetchFavoritesError"));
  } finally {
    loading.value = false;
  }
};

const handlePageChange = (page) => {
  fetchFavoriteCourses(page);
};

const goToCourse = (courseId) => {
  router.push(`/course/${courseId}`);
};

const toggleFavorite = async (courseId) => {
  try {
    await msCourseAPI.removeFavorite(courseId);
    message.success(t("favoriteList.removedFromFavorites"));
    await fetchFavoriteCourses(currentPage.value);
  } catch (error) {
    message.error(t("favoriteList.removeFavoriteError"));
  }
};

onMounted(() => {
  fetchFavoriteCourses(currentPage.value);
});
</script>

<style scoped>
.dashboard-page {
  min-height: 100vh;
  background: #f9f9f9;
  padding-top: 58px;
}

.app-container {
  display: flex;
  min-height: calc(100vh - 58px);
}

.main-content {
  flex: 1;
  height: calc(100vh - 58px);
  padding: 36px 16px 56px;
  overflow-y: auto;
}

.dashboard-content {
  max-width: 950px;
  margin: 30px auto;
  display: flex;
  flex-direction: column;
}

@media (max-width: 1366px) {
  .dashboard-content {
    margin: 50px 0 0 auto;
  }
}

@media (max-width: 1280px) {
  .dashboard-content {
    max-width: calc(100% - 220px);
  }
}

.welcome-section {
  margin-bottom: 24px;
}

.welcome-section h1 {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 4px;
  color: #184387;
}

.section-container {
  padding: 0;
}

.courses-table-container {
  background: #fff;
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.table-wrapper {
  width: 100%;
}

.table-scroll-container {
  overflow-x: auto;
}

.course-link-button {
  border: 0;
  background: transparent;
  padding: 0;
  color: #022964;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
}

.course-link-button:hover {
  color: #0079bb;
}

.remove-favorite-btn {
  padding: 0;
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
}

.pagination-info {
  color: #999;
  font-size: 14px;
}

.pagination-spacer {
  flex-grow: 1;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
}

:deep(.ant-table-wrapper .ant-table-thead > tr > th) {
  background-color: #ffffff !important;
  font-weight: 600;
  color: #333;
  padding: 12px 16px;
}

:deep(.ant-table-wrapper td.ant-table-column-sort) {
  background-color: transparent !important;
}

:deep(.ant-table-wrapper .ant-table-column-sort) {
  background-color: #ffffff !important;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background-color: #e9f1fe !important;
}

:deep(.ant-table-row-selected > td) {
  background-color: #e9f1fe !important;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding: 24px 16px 36px;
  }

  .dashboard-content {
    margin: 0 auto;
    max-width: 100%;
  }

  :deep(.ant-table) {
    width: 100%;
    overflow-x: auto;
  }

  :deep(.ant-table-thead > tr > th),
  :deep(.ant-table-tbody > tr > td) {
    white-space: nowrap;
    padding: 8px 12px;
  }
}

.search-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 400px;
  height: 40px;
  background: #ffffff;
  border: 1px solid #b4b4b4;
  border-radius: 6px;
  box-sizing: border-box;

  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    color: #696969;
    pointer-events: none;

    svg {
      width: 20px !important;
      height: 20px !important;
      display: block;
    }
  }

  input {
    width: 100%;
    height: 100%;
    border: none;
    background: transparent;
    outline: none;
    padding: 0 12px 0 38px;
    font-size: 16px !important;
    font-family: inherit !important;
    color: #1a1a1a !important;
    box-sizing: border-box;
    &::placeholder {
      color: #696969;
    }
  }
}

.custom-select-wrap {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 140px;
  height: 40px;
  background: #ffffff;
  border: 1px solid #b4b4b4;
  border-radius: 6px;
  padding: 0 12px;
  box-sizing: border-box;

  .select-label {
    font-size: 16px !important;
    color: #1a1a1a;
    white-space: nowrap;
    user-select: none;
  }

  .select-divider {
    margin: 0 8px;
    color: #b4b4b4;
    font-size: 16px !important;
    user-select: none;
  }

  select.select-input {
    flex: 1;
    height: 100%;
    border: none;
    background: transparent !important;
    outline: none;
    font-size: 16px !important;
    font-family: inherit !important;
    color: #1a1a1a !important;
    cursor: pointer;
    padding-right: 20px;
    appearance: none;
    -webkit-appearance: none;

    option {
      color: #1a1a1a;
      background: #ffffff;
      font-size: 16px !important;
    }
  }

  .select-arrow {
    position: absolute;
    right: 12px;
    pointer-events: none;
    display: flex;
    align-items: center;
  }
}
</style>
